/**
 * TanStack-backed library hooks for server state (agents catalog,
 * conversation list, single-conversation history, delete mutation).
 *
 * These hooks live in the provider layer so they can both call into the
 * configured `Transport` and dispatch into the shell reducer when server
 * state lands. The reducer's catalog slot (`agents`, `conversations`)
 * mirrors the TanStack cache; selectors and log payloads read from the
 * reducer without re-querying.
 *
 * The library does not own a `QueryClient` — consumers must render
 * `<QueryClientProvider>` above `<MooChatProvider>` (the console host
 * already does this).
 */
import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MooAnalyticsEvents } from '@cowbell/data/analytics';
import { useMooChatContext, useMooConfig } from './internal-context';
import { useShellSelection } from './hooks';
import type {
  ConversationRef,
  ListStartersInput,
  SlashCommand,
} from '../types/transport';

const KEY_AGENTS = ['moo', 'agents'] as const;
const keyConversations = (agentId?: string) =>
  ['moo', 'conversations', agentId ?? '__all__'] as const;
export const keyConversation = (id: string) =>
  ['moo', 'conversation', id] as const;
const keyStarters = (input: import('../types/transport').ListStartersInput) =>
  [
    'moo',
    'starters',
    input.agentId,
    input.view ?? '',
    input.subTab ?? '',
    input.entityType ?? '',
    input.entityId ?? '',
    input.entityName ?? '',
  ] as const;

/** Separator for the allow-list dependency key below. A NUL byte can't occur
 *  in an agent id, so joining on it can't collide two different lists. */
const ALLOWED_IDS_SEP = '\u0000';

/** Fetches the agents catalog and seeds the reducer's `agents` slot.
 *  Default agent is the first returned id.
 *
 *  When the host supplies a non-empty `config.allowedAgentIds`, the catalog
 *  is narrowed to those ids before it's dispatched — so the drawer, the
 *  "Browse all agents" view and the default-agent pick all work from the
 *  same reduced set. The filter runs on the dispatch side rather than in
 *  `queryFn` on purpose: the host reads its allow-list from a feature flag
 *  that can resolve *after* the catalog request does, and a cached query
 *  wouldn't re-run its `queryFn` when it lands.
 *
 *  `config.hiddenAgentIds` removes ids first, the same way and for the same
 *  reason: it comes from a feature flag too. */
export function useAgentsCatalog() {
  const { transport, allowedAgentIds, hiddenAgentIds } = useMooConfig();
  const { dispatch } = useMooChatContext();
  const query = useQuery({
    queryKey: KEY_AGENTS,
    queryFn: () => transport.listAgents(),
    staleTime: 5 * 60 * 1000,
  });
  const { data, status, error } = query;
  // Depend on the allow-list's *contents*, not its array identity: a host
  // passing a fresh array each render would otherwise re-dispatch
  // AGENTS_LOADED on every render (new state -> render -> dispatch -> …).
  const allowedKey = allowedAgentIds?.length
    ? allowedAgentIds.join(ALLOWED_IDS_SEP)
    : '';
  const hiddenKey = hiddenAgentIds?.length
    ? hiddenAgentIds.join(ALLOWED_IDS_SEP)
    : '';
  useEffect(() => {
    if (status === 'success' && data) {
      const agents = filterAllowedAgents(
        dropHiddenAgents(data, hiddenKey),
        allowedKey
      );
      dispatch({
        type: 'AGENTS_LOADED',
        agents,
        defaultAgentId: agents[0]?.id ?? null,
      });
    } else if (status === 'error') {
      // Surface the failure to the reducer so consumers gated on a
      // terminal catalog state (e.g. `useStorageHydration`) can proceed.
      // Without this dispatch the slot stays at `idle` forever and
      // `storageHydrated` never flips after a catalog failure.
      dispatch({
        type: 'AGENTS_LOAD_FAILED',
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }, [status, data, error, dispatch, allowedKey, hiddenKey]);
  return query;
}

/** Removes the ids in `hiddenKey` (the joined deny-list) from `agents`. */
function dropHiddenAgents<T extends { id: string }>(
  agents: T[],
  hiddenKey: string
): T[] {
  if (!hiddenKey) return agents;
  const hidden = new Set(hiddenKey.split(ALLOWED_IDS_SEP));
  return agents.filter((a) => !hidden.has(a.id));
}

/** Narrows `agents` to the ids in `allowedKey` (the joined allow-list).
 *
 *  Fails **open** — an empty key ("no allow-list configured") and an
 *  allow-list that matches nothing in the catalog both pass the full catalog
 *  through. The flag exists to trim the panel down for a specific audience,
 *  not to lock anyone out, so a stale or mistyped id must never be able to
 *  leave someone with an empty agent picker. */
function filterAllowedAgents<T extends { id: string }>(
  agents: T[],
  allowedKey: string
): T[] {
  if (!allowedKey) return agents;
  const allowed = new Set(allowedKey.split(ALLOWED_IDS_SEP));
  const filtered = agents.filter((a) => allowed.has(a.id));
  return filtered.length > 0 ? filtered : agents;
}

/** Fetches the conversation list (cross-agent when `agentId` is omitted)
 *  and upserts refs into the reducer's `conversations` slot. */
export function useConversations(agentId?: string) {
  const { transport } = useMooConfig();
  const { dispatch } = useMooChatContext();
  const query = useQuery({
    queryKey: keyConversations(agentId),
    queryFn: () => transport.listConversations({ agentId }),
    staleTime: 60 * 1000,
  });
  const { data, status } = query;
  useEffect(() => {
    if (status === 'success' && data) {
      const refs: ConversationRef[] = data.conversations.map((c) => ({
        id: c.id,
        agentId: c.agentId,
        title: c.title,
        updatedAt: c.updatedAt ?? '',
      }));
      dispatch({ type: 'CONVERSATION_LIST_LOADED', conversations: refs });
    }
  }, [status, data, dispatch]);
  return query;
}

/** Fetches the agent's conversation-starter suggestions for the current
 *  screen / entity context. Disabled when `agentId` is null. The query key
 *  includes every context dimension so per-view caching is automatic. */
export function useStarters(
  agentId: string | null,
  ctx: Omit<ListStartersInput, 'agentId'> = {}
) {
  const { transport } = useMooConfig();
  const id = agentId ?? '';
  return useQuery({
    queryKey: keyStarters({ agentId: id, ...ctx }),
    queryFn: () => transport.listStarters({ agentId: id, ...ctx }),
    enabled: agentId !== null,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/** Loads a single conversation's history. Returns AI-SDK-compatible
 *  message parts so `useChat({ initialMessages })` can consume the result
 *  directly. Disabled when `id` is null. */
export function useLoadConversation(id: string | null) {
  const { transport } = useMooConfig();
  const safeId = id ?? '';
  return useQuery({
    queryKey: keyConversation(safeId),
    queryFn: () => transport.getConversation(safeId),
    enabled: id !== null,
    staleTime: 5 * 60 * 1000,
  });
}

/** Deletes a conversation. Dispatches `CONVERSATION_DELETED` optimistically;
 *  the reducer also nulls `selection.conversationId` if the deleted id was
 *  open, so `ChatPanel` will remount to the fresh state.
 *
 *  On failure: `onSettled` invalidates the list, which triggers a refetch
 *  that re-seeds the row via `CONVERSATION_LIST_LOADED`. `onError` then
 *  restores `selection.conversationId` if the deleted conversation had
 *  been open (snapshot taken in `onMutate`) — otherwise the user is left
 *  stranded on a fresh chat even though the server still has the
 *  conversation. */
export function useDeleteConversation() {
  const { transport, logger, analytics } = useMooConfig();
  const { state, dispatch } = useMooChatContext();
  const qc = useQueryClient();
  return useMutation<void, unknown, string, { wasOpen: boolean }>({
    mutationFn: (id: string) => transport.deleteConversation(id),
    onMutate: (id) => {
      const wasOpen = state.selection.conversationId === id;
      dispatch({ type: 'CONVERSATION_DELETED', conversationId: id });
      return { wasOpen };
    },
    onSuccess: (_data, id) => {
      logger.debug('moo.conversation.deleted', { conversationId: id });
      analytics.track(MooAnalyticsEvents.CONVERSATION_DELETED, {
        conversationId: id,
      });
    },
    onError: (_err, id, ctx) => {
      if (ctx?.wasOpen) {
        dispatch({ type: 'CONVERSATION_SELECTED', conversationId: id });
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['moo', 'conversations'] });
    },
  });
}

const keySlashCommands = (agentId: string) =>
  ['moo', 'slashCommands', agentId] as const;

/** Internal hook — NOT exported from the library public API.
 *  Fetches slash commands for the currently selected agent when the
 *  transport implements `getSlashCommands`. Returns [] otherwise. */
export function useAgentSlashCommands(): SlashCommand[] {
  const { transport } = useMooConfig();
  const { agentId } = useShellSelection();
  const id = agentId ?? '';
  const { data = [] } = useQuery({
    queryKey: keySlashCommands(id),
    queryFn: () => transport.getSlashCommands!(id),
    enabled:
      agentId !== null && typeof transport.getSlashCommands === 'function',
    staleTime: 5 * 60 * 1000,
  });
  return data;
}
